// CONTRACT:
// PURPOSE: [Inline 4-column breakdown of trust index components для rating company card]
// SCOPE: [rating card, breakdown, expandable]
// INPUT: [rating, reviewsCount, status, registrationDate, inn, ogrn, directorName, address, responseScore]
// OUTPUT: [4-column grid with progress bars and formal data checklist]
// KEYWORDS: [DOMAIN(SEO): index-breakdown; CONCEPT(scoring): formal-data, market-experience, reviews, response; UI: grid, progress-bar, checklist]

"use client";

import { Shield, CheckCircle, AlertCircle } from "lucide-react";

// # START_INTERFACE_CompanyIndexBreakdownProps
interface CompanyIndexBreakdownProps {
  rating: number | null;
  reviewsCount: number | null;
  status: string | null;
  registrationDate: string | null;
  inn: string | null;
  ogrn: string | null;
  directorName: string | null;
  address: string | null;
  responseScore?: number | null;
}
// # END_INTERFACE_CompanyIndexBreakdownProps

// # START_FUNCTION_calculateYears
function calculateYears(registrationDate: string | null): number {
  if (!registrationDate) return 0;
  return (
    (Date.now() - new Date(registrationDate).getTime()) /
    (365.25 * 24 * 60 * 60 * 1000)
  );
}
// # END_FUNCTION_calculateYears

// # START_FUNCTION_calculateFormalScore
function calculateFormalScore(props: {
  status: string | null;
  registrationDate: string | null;
  inn: string | null;
  ogrn: string | null;
  directorName: string | null;
  address: string | null;
}): { score: number; checklist: { label: string; ok: boolean }[] } {
  const years = calculateYears(props.registrationDate);
  const checklist = [
    { label: "Статус", ok: props.status === "ACTIVE" },
    { label: "Возраст", ok: years >= 1 },
    { label: "ИНН", ok: !!props.inn },
    { label: "ОГРН", ok: !!props.ogrn },
    { label: "Руководитель", ok: !!props.directorName },
    { label: "Адрес", ok: !!props.address },
  ];
  const score = Math.round(
    (checklist.filter((d) => d.ok).length / checklist.length) * 100
  );
  return { score, checklist };
}
// # END_FUNCTION_calculateFormalScore

// # START_FUNCTION_BreakdownColumn
/**
 * Одна колонка разбивки индекса.
 * Показывает название, вес, значение и прогресс-бар.
 */
function BreakdownColumn({
  name,
  weight,
  value,
  maxValue = 100,
  gradientFrom = "from-green-400",
  gradientTo = "to-emerald-300",
}: {
  name: string;
  weight: number;
  value: number;
  maxValue?: number;
  gradientFrom?: string;
  gradientTo?: string;
}) {
  const percent = Math.min(100, Math.round((value / maxValue) * 100));

  return (
    <div className="flex flex-col gap-1.5">
      {/* top: name + weight */}
      <div className="flex justify-between items-baseline gap-2">
        <span className="text-[11px] text-white/60 font-mono uppercase tracking-wide">
          {name}
        </span>
        <span className="text-[10px] text-white/40 font-mono whitespace-nowrap">
          вес {weight}%
        </span>
      </div>
      {/* value */}
      <div className="text-xl font-black font-mono text-white">
        {percent}
      </div>
      {/* progress bar */}
      <div className="h-1 bg-white/10 relative overflow-hidden">
        <div
          className={`absolute inset-y-0 left-0 bg-gradient-to-r ${gradientFrom} ${gradientTo}`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}
// # END_FUNCTION_BreakdownColumn

// # START_FUNCTION_FormalDataColumn
/**
 * Колонка «Формальные данные» — показывает formalScore + мини-чеклист ✅/❌.
 */
function FormalDataColumn({
  score,
  checklist,
}: {
  score: number;
  checklist: { label: string; ok: boolean }[];
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {/* top: name + weight */}
      <div className="flex justify-between items-baseline gap-2">
        <span className="text-[11px] text-white/60 font-mono uppercase tracking-wide">
          Формальные данные
        </span>
        <span className="text-[10px] text-white/40 font-mono whitespace-nowrap">
          вес 25%
        </span>
      </div>
      {/* value: score */}
      <div className="text-xl font-black font-mono text-white">
        {score}
      </div>
      {/* progress bar */}
      <div className="h-1 bg-white/10 relative overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-400 to-emerald-300"
          style={{ width: `${score}%` }}
        />
      </div>
      {/* mini-checklist 2x3 grid */}
      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 mt-1">
        {checklist.map((item) => (
          <span
            key={item.label}
            className={`text-[9px] font-mono ${
              item.ok ? "text-green-400/80" : "text-white/20"
            }`}
          >
            {item.ok ? "✅" : "❌"} {item.label}
          </span>
        ))}
      </div>
    </div>
  );
}
// # END_FUNCTION_FormalDataColumn

// # START_FUNCTION_ResponseColumn
/**
 * Колонка «Реакция на жалобы». Если responseScore null — показывает "Скоро".
 */
function ResponseColumn({
  responseScore,
}: {
  responseScore: number | null | undefined;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {/* top: name + weight */}
      <div className="flex justify-between items-baseline gap-2">
        <span className="text-[11px] text-white/60 font-mono uppercase tracking-wide">
          Реакция на жалобы
        </span>
        <span className="text-[10px] text-white/40 font-mono whitespace-nowrap">
          вес 20%
        </span>
      </div>
      {responseScore != null ? (
        <>
          <div className="text-xl font-black font-mono text-white">
            {Math.min(100, Math.round(responseScore))}
          </div>
          <div className="h-1 bg-white/10 relative overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-400 to-emerald-300"
              style={{ width: `${Math.min(100, responseScore)}%` }}
            />
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-1.5 mb-1">
            <AlertCircle className="w-3 h-3 text-white/20" />
            <span className="text-[10px] text-white/20 font-mono italic">
              Скоро
            </span>
          </div>
          <div className="h-1 bg-white/10 relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-400 to-emerald-300" style={{ width: "0%" }} />
          </div>
        </>
      )}
    </div>
  );
}
// # END_FUNCTION_ResponseColumn

// # START_FUNCTION_MarketExperienceColumn
/**
 * Колонка «Опыт на рынке» — рассчитывается из registration_date.
 * min(100, years * 7) — 14+ лет = полный балл.
 */
function MarketExperienceColumn({
  registrationDate,
}: {
  registrationDate: string | null;
}) {
  const years = calculateYears(registrationDate);
  const value = Math.min(100, Math.round(years * 7));

  return (
    <div className="flex flex-col gap-1.5">
      {/* top: name + weight */}
      <div className="flex justify-between items-baseline gap-2">
        <span className="text-[11px] text-white/60 font-mono uppercase tracking-wide">
          Опыт на рынке
        </span>
        <span className="text-[10px] text-white/40 font-mono whitespace-nowrap">
          вес 20%
        </span>
      </div>
      {/* value */}
      <div className="text-xl font-black font-mono text-white">
        {value}
      </div>
      {/* progress bar */}
      <div className="h-1 bg-white/10 relative overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-400 to-emerald-300"
          style={{ width: `${value}%` }}
        />
      </div>
      {/* years hint */}
      <span className="text-[9px] text-white/25 font-mono">
        {years > 0 ? `${Math.round(years)} лет на рынке` : "Нет данных"}
      </span>
    </div>
  );
}
// # END_FUNCTION_MarketExperienceColumn

// # START_FUNCTION_CompanyIndexBreakdown
/**
 * Компактная 4-колоночная сетка разбивки индекса.
 * Inline — без заголовка и общего балла (они в карточке).
 * Desktop: 4 колонки, Mobile: 2 колонки.
 */
export function CompanyIndexBreakdown(props: CompanyIndexBreakdownProps) {
  const reviewsValue =
    props.rating != null && props.rating > 0
      ? Math.round((props.rating / 5) * 100)
      : 0;

  const { score: formalScore, checklist } = calculateFormalScore(props);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
      {/* 1. Отзывы заказчиков — вес 35% */}
      <BreakdownColumn
        name="Отзывы заказчиков"
        weight={35}
        value={reviewsValue}
      />

      {/* 2. Формальные данные — вес 25% (с чеклистом) */}
      <FormalDataColumn score={formalScore} checklist={checklist} />

      {/* 3. Опыт на рынке — вес 20% */}
      <MarketExperienceColumn registrationDate={props.registrationDate} />

      {/* 4. Реакция на жалобы — вес 20% */}
      <ResponseColumn responseScore={props.responseScore} />
    </div>
  );
}
// # END_FUNCTION_CompanyIndexBreakdown
